import {defaultConditon} from '@/context/Promotion/PromotionContext'
import {isStartDateLessThanCurrentDate} from '@/helpers/dateUtils'
import {ConditionGroup, PromotionCondition, PromotionStep, PromotionType} from '@/types/promotion'
import {SegmentTypes} from '../constants/promotionConstants'
import {deepMergeNoArray} from '../misc'
import {getValidPromotionConditions} from './promotionsValidationSchema'

const isEmpty = val => val == null || (typeof val === 'string' && !val.trim())

const hasConsistentRewardTypes = promotionConditions => {
  const rewardTypes = promotionConditions.map(c => c?.rewards?.[0]?.type).filter(Boolean)
  return new Set(rewardTypes).size <= 1
}

const isStepSetupInvalid = (promotion, promotionConfig) => {
  if (!promotion?.name) return true

  const condition = promotion?.promotionConditions?.[0]
  if (!condition) return true

  const {validCount, totalCount} = getValidPromotionConditions(promotion.promotionConditions, promotion.promotionType)

  //When we save a promotion, we want to ensure that all the conditions are valid
  if (validCount !== totalCount) {
    return true
  }

  const conditions = promotion?.promotionConditions || []
  if (conditions.length > 1 && !hasConsistentRewardTypes(conditions)) {
    return true
  }

  // Check if any reward's discount percentage exceeds the maximum allowed
  const maxDiscountPercentage = promotionConfig?.maxDiscountPercentage || 0
  const hasExceededMaxDiscount = conditions.some(condition => {
    return condition?.rewards?.some(reward => {
      return reward?.discountType === 'PERCENTAGE' && reward?.discountValue > maxDiscountPercentage
    })
  })

  const promotionType = promotion?.promotionType
  const shouldCheckForMaxDiscount = [PromotionType.BUY_SINGLE_PRODUCT, PromotionType.BUY_MULTIPLE_PRODUCTS].includes(
    promotionType
  )

  if (shouldCheckForMaxDiscount && hasExceededMaxDiscount) {
    return true
  }
  const segmentType = promotion?.segments?.selectedSegments?.[0]?.selectionType ?? SegmentTypes.ALL_CUSTOMERS
  if (
    promotion?.segments?.selectedSegments?.[0]?.segmentsView?.length === 0 &&
    segmentType !== SegmentTypes.ALL_CUSTOMERS
  ) {
    return true
  }

  // Validate promotion condition values for layered promotions
  if (promotion?.promotionType === PromotionType.BUY_SINGLE_PRODUCT) {
    for (let i = 0; i < conditions.length; i++) {
      if (i == 0) continue 

      // current condition values 
      const currentBuyQuantity = conditions[i]?.conditionGroup?.conditions?.[0]?.quantity
      const currentReward = conditions[i]?.rewards?.[0] || []
      const currentRewardValue = currentReward?.discountValue || currentReward?.points || currentReward?.quantity

      // previous condition values 
      const prevCondition = conditions[i - 1]
      const prevBuyQuantity = prevCondition?.conditionGroup?.conditions?.[0]?.quantity
      const prevReward = prevCondition?.rewards?.[0] || []
      const prevRewardValue = prevReward?.discountValue || prevReward?.points || prevReward?.quantity
      
      if (prevBuyQuantity == null && typeof currentBuyQuantity === 'number') {
        return true
      }

      if (typeof prevBuyQuantity === 'number' && typeof currentBuyQuantity === 'number' && currentBuyQuantity <= prevBuyQuantity) {
        return true
      }

      if (!currentRewardValue) return true

      if (currentRewardValue <= prevRewardValue) return true
    }
  }

  return false
}

const isStepDisplayInvalid = (promotion, languages) => {
  if (isEmpty(promotion?.bannerUrl)) return true

  if (promotion?.bannerType === 'clickable' && !promotion?.landingPage) {
    return true
  }

  if (
    promotion?.displayOrder === false ||
    promotion?.displayOrder === 0 ||
    promotion?.displayOrder === null ||
    promotion?.displayOrder === undefined
  ) {
    return true
  }

  const text = promotion?.text || {}
  const isTextComplete = lang => {
    const localized = text[lang]
    return localized?.title && localized?.bannerTag && localized?.productTag
  }

  const langCodes = languages?.length ? languages.map(l => l.code) : Object.keys(text)

  return !langCodes.every(isTextComplete)
}

const isStepAttributesInvalid = promotion => {
  if (
    !promotion?.activeStartDate ||
    !promotion?.activeStartTime ||
    !promotion?.activeEndDate ||
    !promotion?.activeEndTime
  ) {
    return true
  }

  if (
    promotion?.activeStartDate >= promotion?.activeEndDate ||
    isStartDateLessThanCurrentDate(promotion.activeStartDate)
  ) {
    return true
  }

  return false
}

export const getIsNextDisabled = (step, promotion, languages, promotionConfig) => {
  switch (step) {
    case PromotionStep.SETUP:
      return isStepSetupInvalid(promotion, promotionConfig)
    case PromotionStep.DISPLAY:
      return isStepDisplayInvalid(promotion, languages)
    case PromotionStep.ATTRIBUTES:
      return isStepAttributesInvalid(promotion)
    default:
      return false
  }
}

//For drafts we can save with partial conditions but don't want to return them in the draft
export const parsePromotionDraft = promotion => {
  const parsedPromotion = JSON.parse(JSON.stringify(promotion))
  const {hasValid} = getValidPromotionConditions(promotion.promotionConditions, promotion.promotionType)
  parsedPromotion.promotionConditions = hasValid ? promotion.promotionConditions : []
  return parsedPromotion
}

export function mergePromotionConditions(
  promotionConditions: PromotionCondition[],
  updates: Partial<PromotionCondition>
): PromotionCondition[] {
  return promotionConditions.map(pc => {
    if (pc.sequence !== updates.sequence) return pc

    // 1) Merge top-level fields
    const mergedPC: PromotionCondition = deepMergeNoArray(pc, updates)

    // 2) Merge nested conditionGroup.conditions by index, if provided
    if (updates.conditionGroup?.conditions) {
      const originalConds = pc.conditionGroup?.conditions ?? []
      const incomingConds = updates.conditionGroup.conditions

      mergedPC.conditionGroup = {
        ...(pc.conditionGroup ?? {}),
        ...updates.conditionGroup,
        conditions: originalConds.length
          ? originalConds.map((orig, idx) => deepMergeNoArray(orig, incomingConds[idx] ?? {}))
          : incomingConds,
      } as ConditionGroup
    }

    // 3) Merge rewards array by index, if provided
    if (updates.rewards) {
      const originalRewards = pc.rewards ?? []
      const incomingRewards = updates.rewards

      mergedPC.rewards = originalRewards.length
        ? originalRewards.map((orig, idx) => deepMergeNoArray(orig, incomingRewards[idx] ?? {}))
        : incomingRewards
    }

    return mergedPC
  })
}

export const createPromotionConditions = (type: PromotionType) => {
  switch (type) {
    case PromotionType.BUY_SINGLE_PRODUCT:
      return [
        {
          sequence: 1,
          conditionGroup: {
            conditions: [defaultConditon],
          },
        },
      ]
    case PromotionType.BUY_MULTIPLE_PRODUCTS:
      return [
        {
          sequence: 1,
          conditionGroup: {
            conditions: [defaultConditon, defaultConditon],
          },
        },
      ]
    default:
      return [
        {
          sequence: 1,
        },
      ]
  }
}
const rewardValueKeyMap: Record<string, string> = {
  POINTS: 'points',
  DISCOUNT: 'discountValue',
  AMOUNT: 'amount',
}

const layeredRewardKeyMap: Record<string, string> = {
  POINTS: 'layeredPoints',
  AMOUNT: 'layeredDiscountValue',
  PERCENTAGE: 'layeredDiscountValue',
}

export const transformToLayeredPromotion = (promotion: any) => {
  const conditionsArray = promotion?.promotionConditions || []

  if (!Array.isArray(conditionsArray) || conditionsArray.length === 0) {
    return promotion
  }

  const firstLayer = conditionsArray[0]
  const baseCondition = firstLayer?.conditionGroup?.conditions?.[0]
  const baseReward = firstLayer?.rewards?.[0]
  const isSingleLayer = conditionsArray.length === 1

  const {quantity: _quantity, ...baseConditionWithoutQuantity} = baseCondition ?? {}

  const quantityField = isSingleLayer
    ? {quantity: baseCondition?.quantity}
    : {
        layeredQuantity: conditionsArray
          .map(layer => layer?.conditionGroup?.conditions?.[0]?.quantity)
          .filter((q): q is number => typeof q === 'number'),
      }

  const rewardValueKey = isSingleLayer
    ? rewardValueKeyMap?.[baseReward?.type]
    : layeredRewardKeyMap?.[baseReward?.discountType] || 'layeredPoints'

  const rewardValue = isSingleLayer
    ? (baseReward?.[rewardValueKey] ?? null)
    : conditionsArray
        .map(layer => {
          const reward = layer?.rewards?.[0]
          const key = rewardValueKeyMap?.[reward?.type]
          return reward?.[key] ?? null
        })
        .filter((v): v is number => typeof v === 'number')

  const reward =
    baseReward?.type === 'FREE_PRODUCT'
      ? firstLayer?.rewards?.[0]
      : {
          type: baseReward?.type,
          discountType: baseReward?.discountType,
          [rewardValueKey]: rewardValue,
          discountAppliedOn: isSingleLayer ? baseReward?.discountAppliedOn : null,
        }

  const newPromotion = {
    ...promotion,
    promotionCondition: {
      conditionGroup: {
        conditions: [
          {
            ...(isSingleLayer ? baseCondition : baseConditionWithoutQuantity),
            ...quantityField,
            items: baseCondition?.items || [],
          },
        ],
      },
      reward,
    },
  }

  delete newPromotion.promotionConditions
  delete newPromotion.promotionType

  return newPromotion
}

export const transformToMultiLayerPromotion = (promotion: any) => {
  const layered = promotion?.promotionCondition
  const promotionType = promotion?.uiPromotionType

  if (!layered?.conditionGroup) return promotion

  const baseCondition = layered.conditionGroup.conditions?.[0]
  const reward = layered.reward

  if (!baseCondition || !reward) return promotion

  const {layeredQuantity} = baseCondition

  const isLayered =
    Array.isArray(layeredQuantity) &&
    (Array.isArray(reward?.layeredDiscountValue) || Array.isArray(reward?.layeredPoints))

  const isSingle = typeof baseCondition.quantity === 'number'

  if (!isLayered && !isSingle) return promotion

  const quantities = isLayered ? layeredQuantity : [baseCondition.quantity]

  const rewardValueKey = rewardValueKeyMap?.[reward?.type]

  const layeredRewardValues = reward?.layeredPoints ?? reward?.layeredDiscountValue ?? null

  const rewardValues = isLayered ? layeredRewardValues : [rewardValueKey ? (reward?.[rewardValueKey] ?? null) : null]

  const promotionConditions = quantities.map((qty: number, index: number) => {
    const baseConditionClean = {
      ...baseCondition,
      type: 'BUY',
      quantity: qty,
      items: baseCondition.items || [],
    }

    // FREE_PRODUCT case
    if (reward?.type === 'FREE_PRODUCT') {
      return {
        sequence: index + 1,
        conditionGroup: {
          conditions: [baseConditionClean],
        },
        rewards: [reward],
      }
    }

    return {
      sequence: index + 1,
      conditionGroup: {
        conditions: [baseConditionClean],
      },
      rewards: [
        {
          type: reward?.type,
          discountType: reward?.discountType,
          ...(rewardValueKey && {
            [rewardValueKey]: rewardValues?.[index] ?? null,
          }),
          discountAppliedOn: reward?.discountAppliedOn ?? null,
        },
      ],
    }
  })

  const newPromotion = {
    ...promotion,
    promotionConditions,
    promotionType,
  }

  delete newPromotion.promotionCondition
  delete newPromotion.uiPromotionType

  return newPromotion
}
